import { ConsentType } from "@/api/generated/models";
import type { ConsentKind } from "@/store/medicationHistory";

/**
 * Copy for the medication-history card. Kept in the frontend, beside the other consent surfaces,
 * so the model can never write or paraphrase what is being agreed to.
 */
export type MedicationHistoryCopy = {
  /** Header for the consent step. */
  consentQuestion: string;
  /** Header for the confirmation step. */
  pickerQuestion: string;
  /** Shown one after another under the loader while the pull runs; the last one stays. */
  fetchingMessages: string[];
  emptyDetail: string;
  /** Shown when the pull itself failed, which is not the same as finding nothing. */
  failedDetail: string;
  /** Footer of the consent step, left of Skip. The link sits between the two halves. */
  disclosure: string;
  disclosureSuffix: string;
  linkLabel: string;
  linkHref: string;
  /** Behind the info affordance next to the consent question. */
  info: string;
  /** What we record when the patient agrees. */
  consentType: ConsentType;
};

// TODO(TBD-920): placeholder wording. The disclosure, the info text, and the live-visit
// consequence all need legal review before this ships to patients.
const SURESCRIPTS: MedicationHistoryCopy = {
  consentQuestion:
    "Do you consent to sharing your prescription history with us?",
  pickerQuestion: "Which of these meds are you taking right now",
  // The last line stays up for however long the pull takes, so it must not promise it is nearly done.
  fetchingMessages: [
    "Checking your pharmacy records...",
    "This can take a minute or two...",
    "Connecting to Surescripts...",
    "Gathering your prescriptions...",
    "Looking through your fill history...",
    "Matching up your medications...",
    "Still looking...",
    "Double-checking the details...",
    "Thanks for your patience...",
    "Still working on it...",
  ],
  emptyDetail:
    "We didn't find any prescriptions on file. Add anything you're taking below.",
  failedDetail:
    "We couldn't reach your pharmacy records just now. Add anything you're taking below.",
  disclosure: "We'll get it through",
  disclosureSuffix:
    "so you don't have to list your medications. If not, a clinician will go over them with you live.",
  linkLabel: "Surescripts",
  linkHref: "https://www.surescripts.com/",
  info: "Surescripts is the pharmacy network that holds your prescription history. Curai Health retrieves it to save you time and give your clinician a fuller picture.",
  consentType: ConsentType.surescripts_consent,
};

export const MEDICATION_HISTORY_COPY: Record<
  ConsentKind,
  MedicationHistoryCopy
> = {
  surescripts_consent: SURESCRIPTS,
};

export const medicationHistoryCopyFor = (
  consentType: ConsentKind,
): MedicationHistoryCopy => MEDICATION_HISTORY_COPY[consentType];
